import {
  NodeData,
  PeeringProtocol,
  Attachment,
  Cluster,
  TransportType
} from './data';

export type TryTypes = 'types1' | 'types2';

// Message that are sent between peers
export enum NodeMessageTypes {
  // Content messages
  PEER_OBJECT_REQUEST = 'PEER_OBJECT_REQUEST',
  PEER_OBJECT_RESPONSE = 'PEER_OBJECT_RESPONSE',
  OBJECT_NOTIFICATION = 'OBJECT_NOTIFICATION',
  BULK_OBJECT_NOTIFICATION = 'BULK_OBJECT_NOTIFICATION',
  GRAPH_OBJECT_REQUEST = 'GRAPH_OBJECT_REQUEST',
  FETCH_REQUEST = 'FETCH_REQUEST',
  FETCH_RESPONSE = 'FETCH_RESPONSE',
  AGENT_EVENT = 'AGENT_EVENT',
  INGEST_REQUEST = 'INGEST_REQUEST',
  // Peering messages
  CANDIDATE_REQUEST = 'CANDIDATE_REQUEST',
  CANDIDATE_RESPONSE = 'CANDIDATE_RESPONSE',
  CANDIDATE_NOTIFICATION = 'CANDIDATE_NOTIFICATION',
  PEER_DETACH = 'PEER_DETACH',
  PEER_STATE_UPDATE = 'PEER_STATE_UPDATE',
  PEER_OFFER = 'PEER_OFFER',
  PEER_RESPONSE = 'PEER_RESPONSE',
  PEER_READY = 'PEER_READY',
  CLOSE_PEER = 'CLOSE_PEER',
  // Metrics messages
  SPINUP_TIME = 'SPINUP_TIME',
  TRANSFER_RECORD = 'TRANSFER_RECORD',
  CONTENT_NOTIFICATION_RECORD = 'CONTENT_NOTIFICATION_RECORD',
  CONNECTION_RECORD = 'CONNECTION_RECORD',
  PEER_NOTIFICATION_RECORD = 'PEER_NOTIFICATION_RECORD',
  PEER_REQUEST_RECORD = 'PEER_REQUEST_RECORD',
  PEER_STATE_RECORD = 'PEER_STATE_RECORD',
  DETACH_RECORD = 'DETACH_RECORD',
  ATTACHMENT_REQUEST_RECORD = 'ATTACHMENT_REQUEST_RECORD',
  // Misc. agent control messages
  TIMEOUT = 'TIMEOUT',
  TIMER = 'TIMER',
  // Coordinator messages
  COORD_CLUSTER_DETACH = 'COORD_CLUSTER_DETACH',
  NODE_UPDATE = 'NODE_UPDATE',
  ATTACHMENT_REQUEST = 'ATTACHMENT_REQUEST',
  ATTACHMENT_RESPONSE = 'ATTACHMENT_RESPONSE',
  ATTACHMENT_RESPONSE_COMPLETE = 'ATTACHMENT_RESPONSE_COMPLETE',
  CLUSTER_LIST_REQUEST = 'CLUSTER_LIST_REQUEST',
  CLUSTER_LIST_RESPONSE = 'CLUSTER_LIST_RESPONSE',
  ATTACHMENT_STATUS = 'ATTACHMENT_STATUS',
  FINISHED = 'FINISHED'
}

type Message = {}; //eslint-disable-line

export interface AttachmentStatus {
  status: number;
}

export interface PeerOffer {
  from: PeerCandidate;
  to: PeerCandidate;
  type: number;
}

export interface PeerResponse {
  from: PeerCandidate;
  to: PeerCandidate;
  type: number;
  reason: number;
}

export interface PeerReady {
  peerId: string;
}

export interface NodeMessage {
  type: NodeMessageTypes;
  body: Message;
}

export class KeepAlive {}

export interface AgentMessage {
  type: NodeMessageTypes;
  message: Message;
}

export enum AgentEventType {
  SOURCING, // The agent notifies the kernel that the specified object will be added to the cache after an
  // indeterminate amount of time. The source of the object may be local ingest or the graph.

  SOURCED, // The object has been added to the agent's cache

  EVICTED, // The agent has removed the object from the cache

  PRE_CACHE // This object existed in the agent's cache before the kernel was started, no need to send a transfer
  // record.
}

export interface AgentEvent {
  type: AgentEventType;
  objectId: string;
}

export interface BaseContentMessage {
  from: string;
  to: string;
  objectId: string;
  metadata: string;
}

export interface BaseObjectNotification {
  type: number;
  objectId: string;
}

export interface ObjectNotification extends BaseObjectNotification {
  from: string;
  to: string;
}

export interface BulkObjectNotification {
  from: string;
  to: string;
  objects: BaseObjectNotification[];
}

export interface ContentResponse extends BaseContentMessage {
  status: number;
  complete: boolean;
}

export interface DataStart extends BaseContentMessage {
  status: number;
  firstChunk?: ArrayBuffer;
  numChunks: number;
}

export interface DataChunk {
  objectId: string;
  index: number;
  data: ArrayBuffer;
}

export interface IngestRequest {
  objectId: string;
}

export interface FetchRequest {
  objectId: string;
  metadata: string;
}

export interface FetchResponse {
  peerId: string;
  objectId: string;
  reason: number;
  metadata: string;
}

export interface CandidateRequest {
  from: string;
  to: string;
  path: string[];
}

export interface NodeState {
  upCapacity: number;
  midCapacity: number;
  downCapacity: number;
}

export interface PeerCandidate {
  nodeId: string;
  agentId: string;
  tenantId: string;
  contentId: string;
  upstreams: string[];
  depth: number;
  state: NodeState;
  protocol: PeeringProtocol;
}

export class PeerCandidateImpl implements PeerCandidate {
  nodeId: string;
  agentId: string;
  tenantId: string;
  contentId: string;
  upstreams: string[];
  depth: number;
  state: NodeState;
  protocol: PeeringProtocol;

  constructor(
    nodeId: string,
    agentId: string,
    tenantId: string,
    contentId: string,
    upstreams: string[],
    depth: number,
    state: NodeState,
    protocol: PeeringProtocol
  ) {
    this.nodeId = nodeId;
    this.agentId = agentId;
    this.tenantId = tenantId;
    this.contentId = contentId;
    this.upstreams = upstreams;
    this.depth = depth;
    this.state = state;
    this.protocol = protocol;
  }
}

export interface CandidateResponse {
  from: string;
  to: string;
  status: number;
  candidates: PeerCandidate[];
  path: string[];
}

export interface CandidateNotification {
  from: string;
  to: string;
  candidate: PeerCandidate;
}

export interface ClosePeer {
  from: string;
  to: string;
  reason: number;
}

export class ClusterImpl implements Cluster {
  id: string;
  transportType: TransportType;
  protocolMajorVersion: number;
  maxDepth: number;

  constructor(
    id: string,
    transportType: TransportType,
    protocolMajorVersion: number
  ) {
    this.id = id;
    this.transportType = transportType;
    this.protocolMajorVersion = protocolMajorVersion;
    this.maxDepth = 0; // UNUSED - Now included in the node configuration
  }
}

export class AttachmentImpl implements Attachment {
  activeCluster: Cluster;
  upstreams: string[];
  depth: number;

  constructor(activeCluster: Cluster, upstreams: string[], depth: number) {
    this.activeCluster = activeCluster;
    this.upstreams = upstreams;
    this.depth = depth;
  }
}

export class NodeInfoImpl implements NodeData {
  id: string;
  agentId: string;
  tenantId: string;
  contentId: string;
  attachment: Attachment;
  protocol: PeeringProtocol;
  state: NodeState;
  locality: string;

  constructor(
    id: string,
    agentId: string,
    tenantId: string,
    contentId: string,
    attachment: Attachment,
    protocol: PeeringProtocol,
    state: NodeState,
    locality: string
  ) {
    this.id = id;
    this.agentId = agentId;
    this.tenantId = tenantId;
    this.contentId = contentId;
    this.attachment = attachment;
    this.protocol = protocol;
    this.state = state;
    this.locality = locality;
  }
}

export interface PeerClusterDetach {
  peerId: string;
  sourceId: string;
  detachingPeer: NodeData;
}

export interface TimerMessage {
  when: number;
  id: number;
}

export interface NodeUpdate {
  node: NodeData;
}

export interface PeerStateUpdate {
  from: string;
  to: string;
  node: NodeData;
}

export interface CoordinatorClusterDetach {
  node: NodeData;
}

export interface SpinUpTime {
  node: NodeData;
  monotonicTime: number;
  wallClockTime: number;
}

export interface TransferRecord {
  node: NodeData;
  sourcePeer: string;
  objectId: string;
  type: number;
  start: number;
  load: number;
  end: number;
  statusCode: number;
}

export interface ContentNotificationRecord {
  node: NodeData;
  reportingPeer: string;
  objectId: string;
  objectState: number;
  wallReceivedTime: number;
  monotonicReceivedTime: number;
}

export interface PeerStateRecord {
  node: NodeData;
  peer: NodeData;
  wallSentTime: number;
  monotonicSentTime: number;
}

export interface AttachmentResponseComplete {
  status: number;
}

// monostate,
//     content::IngestRequest,
//     content::SourceRequest,
//     content::Request,
//     content::Response,
//     content::ContentNotification,
//     content::FetchFailure,
//     metrics::SpinUpTime,
//     metrics::TransferRecord,
//     metrics::ContentNotificationRecord,
//     metrics::ConnectionRecord,
//     metrics::PeerNotificationRecord,
//     metrics::PeerRequestRecord,
//     metrics::PeerStateRecord,
//     metrics::DetachRecord,
//     metrics::AttachmentRequestRecord,
//     peering::CandidateRequest,
//     peering::CandidateResponse,
//     peering::CandidateNotification,
//     peering::OpenPeer,
//     peering::ClosePeer,
//     peering::ClusterDetach,
//     core::TimerMessage,
//     coordinator::AttachmentRequest,
//     coordinator::NodeUpdate,
//     coordinator::ClusterDetach>;
